import { useRef, useState, useEffect, useCallback } from 'react';

const defaultIntersectionObserverInit = {
  root: null,
  rootMargin: '0px',
  threshold: Array.from({ length: 100 }, (_, i) => i * 0.01),
};

const useVisible = (cb: (visible: number) => boolean, options = {}) => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [visible, setVisible] = useState(0);

  // Memoized callback to update visibility state
  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        setVisible(entry.intersectionRatio);
      });
    },
    [],
  );

  useEffect(() => {
    if (!targetRef.current || observerRef.current) return;

    observerRef.current = new IntersectionObserver(observerCallback, {
      ...defaultIntersectionObserverInit,
      ...options,
    });

    observerRef.current.observe(targetRef.current);

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [observerCallback, options]);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  const status = typeof cb === 'function' ? cb(visible) : visible > 0.5;
  return [targetRef, status] as const;
};

export { useVisible };
